import { ArrowUpRight, ArrowDown } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { useHoverParallax } from "@/lib/useHoverParallax";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Premium editorial hero for inner pages.
 * Concise headline, short value proposition, contextual photograph with
 * grayscale→colour treatment and subtle cursor parallax, primary CTA +
 * optional secondary link.
 */
export default function EditorialHero({
  testid = "editorial-hero",
  eyebrow,
  title,
  description,
  tagline,
  image,
  imageAlt,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  secondaryTo,
}) {
  const { ref, onMove, onLeave } = useHoverParallax(7);

  return (
    <section
      data-testid={testid}
      className="relative pt-32 md:pt-40 pb-16 md:pb-20 bg-[#fafafa] overflow-hidden"
    >
      <div className={`${GRID} grid grid-cols-12 gap-8 md:gap-10 items-center lg:items-stretch`}>
        <div className="col-span-12 lg:col-span-7 relative z-10 stagger">
          <SectionBadge label={eyebrow} />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight text-black max-w-3xl">
            {title}
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-600 leading-relaxed">
            {description}
          </p>
          {tagline && (
            <p className="mt-5 font-serif-display italic text-lg sm:text-xl text-black leading-snug">
              {tagline}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid={`${testid}-cta`}
              onClick={onPrimary}
              className="group/btn inline-flex items-center gap-3 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              {primaryLabel}
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
            </button>
            {secondaryLabel && secondaryTo && (
              <a
                data-testid={`${testid}-secondary`}
                href={secondaryTo}
                className="group/btn inline-flex items-center gap-3 h-12 px-7 border border-black/20 text-black text-[15px] font-medium hover:border-black transition-colors duration-300"
              >
                {secondaryLabel}
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-y-1" />
              </a>
            )}
          </div>
        </div>

        {image && (
          <div
            className="col-span-12 lg:col-span-5 relative flex group"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          >
            <div
              data-testid={`${testid}-visual`}
              className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[420px] w-full overflow-hidden bg-zinc-200 border border-zinc-200"
            >
              <div ref={ref} className="absolute inset-0 parallax-frame">
                <img
                  src={image}
                  alt={imageAlt || title}
                  fetchPriority="high"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
                />
              </div>
              <div className="absolute inset-0 bg-black/10 pointer-events-none" />
              <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-md px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-white">
                {eyebrow}
              </div>
            </div>
            <div
              className="hidden lg:block absolute -z-10 -right-8 -bottom-8 w-40 h-40 border border-black/30"
              aria-hidden
            />
          </div>
        )}
      </div>
    </section>
  );
}
